import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  SignupAlert, SignupAside, SignupChoices, SignupField, SignupHeading, SignupStepper, signupPrimary,
} from '@/components/signup/SignupParts';
import {
  SALES_TEAM_SIZES, SIGNUP_PASSWORD_MIN_LENGTH, SIGNUP_ROLES, isProfessionalEmail, normalizeWebsiteDomain,
  type CompanySignup, type SalesTeamSize, type SignupRole,
} from '@shared/company-signup';

const authMessage = (message: string): string => {
  if (/already registered|already exists/i.test(message)) return 'Un compte existe déjà avec cet email. Connectez-vous plutôt.';
  if (/password/i.test(message)) return `Le mot de passe doit contenir au moins ${SIGNUP_PASSWORD_MIN_LENGTH} caractères.`;
  if (/invalid.*email|email.*invalid/i.test(message)) return 'Cette adresse email n’est pas valide.';
  return 'La création du compte n’a pas abouti. Réessayez dans un instant.';
};

/**
 * Self-serve signup to the free diagnostic (19/10/2026): the company first (one
 * diagnostic per website domain, checked before the account exists), then the
 * account. What was typed rides in the auth metadata `signup`; `/bienvenue`
 * records the company and starts the analyst, also after an email confirmation.
 */
export const SignupPage = () => {
  const [step, setStep] = useState<0 | 1>(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);

  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [size, setSize] = useState<SalesTeamSize>('2-5');
  const [role, setRole] = useState<SignupRole>('head_of_sales');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const submitCompany = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const domain = normalizeWebsiteDomain(website);
    if (!company.trim() || !domain) {
      setError('Indiquez le nom de votre entreprise et l’adresse de son site, par exemple acme.fr.');
      return;
    }
    setBusy(true);
    const { data, error: rpcError } = await supabase.rpc('company_domain_status', { p_website: domain });
    setBusy(false);
    if (rpcError) { setError('La vérification du site n’a pas abouti. Réessayez dans un instant.'); return; }
    if (data === 'taken') {
      setError(`Un diagnostic existe déjà pour ${domain}. Demandez à la personne qui l’a lancé de vous inviter.`);
      return;
    }
    setStep(1);
  };

  const submitAccount = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!isProfessionalEmail(email)) {
      setError('Utilisez votre email professionnel : le diagnostic porte sur votre entreprise.');
      return;
    }
    if (password.length < SIGNUP_PASSWORD_MIN_LENGTH) {
      setError(`Le mot de passe doit contenir au moins ${SIGNUP_PASSWORD_MIN_LENGTH} caractères.`);
      return;
    }
    const signup: CompanySignup = { company: company.trim(), website: normalizeWebsiteDomain(website) ?? website.trim(), size, role };
    setBusy(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: fullName.trim() || undefined, signup },
        emailRedirectTo: `${window.location.origin}/bienvenue`,
      },
    });
    if (signUpError) { setBusy(false); setError(authMessage(signUpError.message)); return; }
    // With a session, the app opens and /signup leads to /bienvenue; otherwise the email link does.
    if (!data.session) { setBusy(false); setConfirmEmail(email.trim()); }
  };

  return (
    <div className="flex min-h-screen flex-wrap bg-domino-ivory font-sans text-domino-ink">
      <SignupAside />
      <main className="flex min-w-0 flex-[999_1_560px] flex-col gap-8 px-6 py-12 md:px-24">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <SignupStepper step={confirmEmail ? 2 : step} />
          <span className="text-sm text-domino-inkSoft">
            Déjà un compte ? <Link to="/login" className="font-semibold text-domino-aubergine">Se connecter</Link>
          </span>
        </div>

        {error && <SignupAlert>{error}</SignupAlert>}

        {confirmEmail ? (
          <section className="flex w-full max-w-[520px] flex-col gap-5">
            <SignupHeading title="Confirmez votre adresse">
              Nous avons envoyé un lien à {confirmEmail}. Ouvrez-le pour lancer votre diagnostic.
            </SignupHeading>
          </section>
        ) : step === 0 ? (
          <form onSubmit={(event) => void submitCompany(event)} className="flex w-full max-w-[560px] flex-col gap-5">
            <SignupHeading title="Parlez-nous de votre entreprise">Gratuit et sans carte bancaire. Un diagnostic par entreprise.</SignupHeading>
            <SignupField id="company" label="Nom de l’entreprise" value={company} onChange={setCompany} autoComplete="organization" required />
            <SignupField id="website" label="Site web" value={website} onChange={setWebsite} autoComplete="url" placeholder="acme.fr" required />
            <SignupChoices legend="Combien de personnes vendent dans l’équipe ?" options={SALES_TEAM_SIZES} value={size} onChange={setSize} />
            <SignupChoices legend="Votre rôle" options={SIGNUP_ROLES} value={role} onChange={setRole} />
            <button type="submit" disabled={busy} className={signupPrimary}>{busy ? 'Vérification…' : 'Continuer'}</button>
          </form>
        ) : (
          <form onSubmit={(event) => void submitAccount(event)} className="flex w-full max-w-[520px] flex-col gap-5">
            <SignupHeading title="Créez votre compte" />
            <SignupField id="name" label="Prénom et nom" value={fullName} onChange={setFullName} autoComplete="name" />
            <SignupField
              id="email" label="Email professionnel" type="email" value={email} onChange={setEmail}
              autoComplete="email" placeholder="prenom@entreprise.fr" required
              hint="Les adresses personnelles (Gmail, Outlook…) ne sont pas acceptées : le diagnostic porte sur votre entreprise."
            />
            <SignupField
              id="password" label="Mot de passe" type="password" value={password} onChange={setPassword}
              autoComplete="new-password" placeholder={`${SIGNUP_PASSWORD_MIN_LENGTH} caractères minimum`} required
            />
            <button type="submit" disabled={busy} className={signupPrimary}>{busy ? 'Création du compte…' : 'Lancer mon diagnostic'}</button>
            <button type="button" onClick={() => { setError(null); setStep(0); }} className="self-start text-sm font-semibold text-domino-aubergine">
              Modifier l’entreprise
            </button>
            <p className="text-[13px] text-domino-inkSoft">
              En créant un compte, vous acceptez les{' '}
              <a href="https://dominoflow.com/conditions" className="text-domino-aubergine underline">conditions</a> et la{' '}
              <a href="https://dominoflow.com/confidentialite" className="text-domino-aubergine underline">politique de confidentialité</a>.
            </p>
          </form>
        )}
      </main>
    </div>
  );
};
